import { useEffect, useRef, useState } from "react";
import { useBackClose } from "@/mobile/backClose";
import { ClockIcon } from "./ScheduleSendMenu";
import { toLocalInput } from "./scheduleTime";
import {
  checkDue,
  checkReminderForm,
  dueUnchanged,
  fmtDue,
  REMINDER_TEXT_MAX,
  reminderErrorText,
  reminderPresets,
} from "./reminders";
import "./reminders.css";

// Форма напоминания: что и когда. Одна на все случаи — правка предложения
// прямо в карточке («Сохранить и принять»), новое по кнопке «Напомнить» и
// «Изменить» в полосе над композером (эти два — в модалке ReminderDialog).
//
// Пресеты — самые частые договорённости в разговоре («перезвоню завтра»,
// «после выходных»); всё остальное — полем даты. Проверка срока та же, что на
// сервере (reminders.ts), и отказ сервера показывается здесь же, под полями.
// Срок, который не трогали, не проверяется и не отправляется: у наступившего
// он уже в прошлом, а поправить текст, не переставляя время (и не рассылая
// напоминание заново), должно быть можно.

export function ReminderEditor({
  text: initialText = "",
  due: initialDue = null,
  submitLabel,
  busy = false,
  error = null,
  autoFocus = false,
  onSubmit,
  onCancel,
}: {
  text?: string;
  /** null — поле пустое, срок выбирают с нуля */
  due?: Date | null;
  submitLabel: string;
  busy?: boolean;
  /** отказ сервера — показывается, пока рекрутёр не тронет форму */
  error?: string | null;
  autoFocus?: boolean;
  onSubmit: (text: string, due: Date) => void;
  onCancel: () => void;
}) {
  const [text, setText] = useState(initialText);
  const [when, setWhen] = useState(() => (initialDue ? toLocalInput(initialDue) : ""));
  const [localError, setLocalError] = useState<string | null>(null);
  // Серверная ошибка относится к тому, что отправили: правка формы её снимает.
  const [serverError, setServerError] = useState(error);
  useEffect(() => setServerError(error), [error]);
  const textRef = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    if (!autoFocus) return;
    const el = textRef.current;
    if (!el) return;
    el.focus();
    el.setSelectionRange(el.value.length, el.value.length);
  }, [autoFocus]);

  const now = new Date();
  const presets = reminderPresets(now);
  const unchanged = dueUnchanged(when, initialDue);
  const hintAt = unchanged ? initialDue : checkDue(when, now).at;
  const shown = localError ?? serverError;

  const touch = () => {
    setLocalError(null);
    setServerError(null);
  };
  const submit = () => {
    const form = checkReminderForm(text, when, initialDue, new Date());
    if (form.error !== null) {
      setLocalError(form.error);
      return;
    }
    onSubmit(form.text, form.due);
  };

  return (
    <form
      className="rem-editor"
      onSubmit={(e) => {
        e.preventDefault();
        if (!busy) submit();
      }}
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          e.stopPropagation();
          onCancel();
        }
      }}
    >
      <textarea
        ref={textRef}
        className="field-input field-textarea rem-editor__text"
        rows={3}
        maxLength={REMINDER_TEXT_MAX}
        placeholder="О чём напомнить…"
        aria-label="Текст напоминания"
        value={text}
        disabled={busy}
        onChange={(e) => {
          setText(e.target.value);
          touch();
        }}
        onKeyDown={(e) => {
          // Enter — новая строка, как в любом многострочном поле; Ctrl+Enter — сохранить.
          if (e.key === "Enter" && (e.ctrlKey || e.metaKey) && !busy) {
            e.preventDefault();
            submit();
          }
        }}
      />
      <div className="rem-editor__when">
        <input
          type="datetime-local"
          className="field-input rem-editor__input"
          aria-label="Когда напомнить"
          value={when}
          // Нетронутый срок в прошлом — не повод браузеру не пустить форму.
          min={unchanged ? undefined : toLocalInput(now)}
          disabled={busy}
          onChange={(e) => {
            setWhen(e.target.value);
            touch();
          }}
        />
        {hintAt && (
          <span className="rem-editor__hint">
            <ClockIcon /> {fmtDue(hintAt.toISOString(), now)}
          </span>
        )}
      </div>
      <div className="rem-editor__presets">
        {presets.map((p) => (
          <button
            key={p.label}
            type="button"
            className="rem-chip"
            disabled={busy}
            onClick={() => {
              setWhen(toLocalInput(p.at));
              touch();
            }}
          >
            {p.label}
          </button>
        ))}
      </div>
      {shown && (
        <div className="rem-editor__err" role="alert">
          {shown}
        </div>
      )}
      <div className="rem-editor__actions">
        <button type="submit" className="rem-btn rem-btn--accent" disabled={busy}>
          {busy ? "Сохраняем…" : submitLabel}
        </button>
        <button type="button" className="rem-btn" disabled={busy} onClick={onCancel}>
          Отмена
        </button>
      </div>
    </form>
  );
}

/** Та же форма в модалке кита: новое напоминание («Напомнить» в шапке чата)
 *  и правка активного из полосы над композером, а на телефоне — и правка из
 *  карточки. `onSubmit` бросает — ошибка остаётся в форме, модалка не
 *  закрывается. На телефоне модалка кита — лист снизу (mobile.css), и
 *  «Назад» закрывает её, а не переписку под ней. */
export function ReminderDialog({
  title,
  text,
  due,
  submitLabel,
  hint,
  error: initialError = null,
  onSubmit,
  onClose,
}: {
  title: string;
  text?: string;
  due?: Date | null;
  submitLabel: string;
  /** Пояснение над формой («Напоминание придёт системным сообщением в чат…»). */
  hint?: string;
  /** с чем окно открылось («время уже прошло — выберите новое») */
  error?: string | null;
  onSubmit: (text: string, due: Date) => Promise<void>;
  onClose: () => void;
}) {
  useBackClose(onClose);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(initialError);
  const save = async (t: string, d: Date) => {
    setBusy(true);
    setError(null);
    try {
      await onSubmit(t, d);
    } catch (e) {
      setError(reminderErrorText(e));
      setBusy(false);
    }
  };
  return (
    <div className="modal-backdrop" onMouseDown={() => !busy && onClose()}>
      <div
        className="modal rem-dialog"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="modal-head">
          <h2 className="modal-title">{title}</h2>
        </div>
        <div className="modal-body">
          {hint && <p className="field-hint">{hint}</p>}
          <ReminderEditor
            text={text}
            due={due}
            submitLabel={submitLabel}
            busy={busy}
            error={error}
            autoFocus
            onSubmit={(t, d) => void save(t, d)}
            onCancel={onClose}
          />
        </div>
      </div>
    </div>
  );
}
